/**
 * The body of the decision sheet: five short sections (what it does, the trade-off, what it can move, how a banker decides, what it moved last time).
 * The drawer wraps it; the walk's "Before you decide" box renders it in place.
 */
import { useState } from "react";
import type { ContentCatalog, DecisionCatalogEntry, MarketView, TeamState } from "@bsim/contracts";
import type { KpiId } from "@bsim/domain";
import { useRoute } from "../api/context";
import { useRoundPosition } from "./useRoundPosition";
import { fiscalYearLabel, formatPrintedChange } from "../format";
import { kpiLabel } from "../format/labels";
import { GxLoading } from "../theme/GxLoading";
import { DecisionContext } from "./context";
import type { RiskProjection } from "../api/riskFields";
import { decisionIdOf } from "./decisionValues";
import { limitsOf } from "./limits";
import { effectLines } from "./effects";
import { outlookLine, ownNumberLines } from "./sheetLines";
import { DriverPanel } from "./drivers/DriverPanel";

/** The two directions along a decision's option order, named by the end each runs toward ("up" = toward the later option). */
function laterLines(entry: DecisionCatalogEntry): readonly { readonly key: string; readonly text: string }[] {
  if (entry.tradeOffs !== undefined) return [{ key: "later", text: entry.tradeOffs.later }];
  const first = Object.values(entry.options)[0] ?? [];
  const [low, high] = [first[0]?.label, first.at(-1)?.label];
  if (entry.deferredNotes !== undefined) return [{ key: "up", text: `${high === undefined ? "Moving up" : `Toward ${high}`}: ${entry.deferredNotes.up}` }, { key: "down", text: `${low === undefined ? "Moving down" : `Toward ${low}`}: ${entry.deferredNotes.down}` }];
  return entry.deferredCostNote === null ? [] : [{ key: "note", text: entry.deferredCostNote }];
}

/** The bank's own books under "What it does": the first few lines, the rest on "Show all". */
const FEW_LINES = 3;
function OwnNumbers({ lines }: { readonly lines: readonly string[] }) {
  const [all, setAll] = useState(false);
  const shown = all ? lines : lines.slice(0, FEW_LINES);
  return (
    <>
      <div className="gx-num gx-sheet-nums" data-testid="decision.sheet.numbers">{shown.map((l) => <p key={l}>{l}</p>)}</div>
      {lines.length <= FEW_LINES ? null : <button type="button" className="gx-decide-linkbtn" aria-expanded={all} data-testid="decision.sheet.numbers.all" onClick={() => setAll(!all)}>{all ? "Show fewer" : `Show all ${lines.length}`}</button>}
    </>
  );
}

function MovedLastTime({ entry, team, catalog }: { readonly entry: DecisionCatalogEntry; readonly team: TeamState; readonly catalog: ContentCatalog }) {
  const last = team.releasedRounds.at(-1);
  const report = useRoute("roundReport", last === undefined ? null : { teamId: team.team.id, roundId: last.roundId }, { staleTime: Infinity });
  if (last === undefined) return <p data-testid="decision.last.none">Not changed before.</p>;
  if (report.error !== null) return <p className="gx-muted" data-testid="decision.last.error">Last year&apos;s effect is not available right now.</p>;
  if (report.data === undefined) return <GxLoading variant="inline" words="Loading last year's effect…" />;
  const attribution = report.data.decisionAttribution;
  if (attribution === undefined) return <p className="gx-muted" data-testid="decision.last.none">Last year&apos;s effect is not recorded.</p>;
  const rows = attribution.rows.filter((r) => typeof r.key === "object" && decisionIdOf(r.key.decision) === entry.decisionId);
  if (rows.length === 0) return <p data-testid="decision.last.none">Not changed before.</p>;
  const moves = attribution.headlineKpis.flatMap((kpi: KpiId) => {
    const delta = rows.reduce((t, r) => t + (r.kpiDeltas[kpi] ?? 0), 0);
    return delta === 0 ? [] : [{ kpi, text: `${kpiLabel(kpi)} ${formatPrintedChange(kpi, delta, { signed: true })}` }];
  });
  return (
    <p data-testid="decision.last.rows">
      <span className="gx-faint">{fiscalYearLabel(catalog, last.roundNo)}: </span>
      {moves.length === 0 ? "No headline measure moved." : moves.map((m) => m.text).join(" · ")}
    </p>
  );
}

export function DecisionSheetBody({ entry, team, catalog, market, projection, panel = true }: { readonly entry: DecisionCatalogEntry; readonly team: TeamState; readonly catalog: ContentCatalog; readonly market: MarketView; readonly projection?: RiskProjection | undefined; /** The drivers panel at the top; the walk shows it inline under the control instead. */ readonly panel?: boolean }) {
  const position = useRoundPosition(team.team.id, team.round?.roundNo ?? 0);
  const numbers = position === undefined ? [] : ownNumberLines(entry, position.products);
  const outlook = outlookLine(entry, market.outlook?.market);
  // A decision with a "no hedge" option beside its numbers has no end of the grid to say a move reaches: no "what it can move" list for it.
  const moves = Object.values(entry.options).every((o) => o.every((x) => typeof x.value === "number")) ? effectLines(entry) : [];
  return (
  <div className="gx-sheet" data-testid="decision.sheet">
    {panel ? <DriverPanel entry={entry} team={team} catalog={catalog} /> : null}
    <section data-testid="decision.sheet.what"><h3 className="gx-eyebrow">What it does</h3><p>{entry.whatItDoes ?? entry.help}</p>{numbers.length === 0 ? null : <OwnNumbers lines={numbers} />}</section>
    <section data-testid="decision.sheet.tradeoff">
      <h3 className="gx-eyebrow">The trade-off</h3>
      {entry.tradeOffs === undefined ? null : <p><b>Now:</b> {entry.tradeOffs.now}</p>}
      {laterLines(entry).map((l) => <p key={l.key}>{l.key === "later" ? <><b>Later:</b> {l.text}</> : l.text}</p>)}
    </section>
    {moves.length === 0 ? null : (
      <section data-testid="decision.sheet.moves">
        <h3 className="gx-eyebrow">What it can move</h3>
        {moves.map((m) => <p key={`${m.kpi}.${m.text}`} data-testid={`decision.sheet.moves.${m.kpi}`}>{m.text}.</p>)}
      </section>
    )}
    <section data-testid="decision.sheet.decides">
      <h3 className="gx-eyebrow">How a banker decides</h3>
      <p>{entry.businessCase ?? "Weigh what it earns now against what it costs later, and set it against where your bank sits among its rivals."}</p>
      <DecisionContext limits={limitsOf(team, catalog)} decisionId={entry.decisionId} lines={entry.context} own={team.kpiHistory.at(-1)?.kpis ?? {}} market={market} projection={projection} />
      {outlook === null ? null : <p data-testid="decision.sheet.outlook">{outlook}</p>}
    </section>
    <section data-testid="decision.sheet.last"><h3 className="gx-eyebrow">What it moved last time</h3><MovedLastTime entry={entry} team={team} catalog={catalog} /></section>
  </div>
  );
}
